import { ColumnDef } from "@tanstack/react-table";
import { DropdownMenu, DropdownMenuOption } from "components/";
import { PaginationProps } from "components/pagination";
import { DataTable } from "components/data-table";
import { useNavigate } from "react-router-dom";
import { Routes } from "router";
import styles from "./styles.module.scss";

export interface CVAnalysisTableItem {
  id: string;
  cvAnalysisId?: string;
  role: string;
  date: string;
  numberOfCVs: number;
  status: "Completed" | "Drafts";
}

interface CVAnalysisTableProps {
  tableBodyItems: CVAnalysisTableItem[];
  handleView: (id: string, cvAnalysisId?: string) => void;
  /** @deprecated No longer used */
  tableBodyItemClassName?: string;
  /** @deprecated No longer used */
  tableBodyRowClassName?: string;
  pagination?: PaginationProps;
  emptyState?: React.ReactNode;
}

const CVAnalysisTable: React.FC<CVAnalysisTableProps> = ({
  tableBodyItems,
  handleView,
  pagination,
  emptyState,
}) => {
  const navigate = useNavigate();

  const handleReviewTalents = (id: string, status: string, cvAnalysisId?: string) => {
    if (status === 'Drafts') {
      navigate(`${Routes.publishAssessment}?id2=${id}`);
    } else {
      navigate(Routes.reviewTalentsCVAnalysis(cvAnalysisId || id));
    }
  };

  const columns: ColumnDef<CVAnalysisTableItem, unknown>[] = [
    {
      id: "sn",
      header: "S/N",
      meta: { className: "w-12 flex-shrink-0 text-[12px] text-[#667085]" },
      cell: ({ row }) => (
        <span className="text-[12px] text-[#667085]">{row.index + 1}</span>
      ),
    },
    {
      id: "role",
      header: "Role",
      meta: { className: "flex-1" },
      cell: ({ row }) => (
        <span
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            handleView(row.original.id, row.original.cvAnalysisId);
          }}
          className={styles.title}
        >
          {row.original.role}
        </span>
      ),
    },
    {
      id: "date",
      header: "Date",
      meta: { className: "flex-1 text-[12px] text-[#667085]" },
      cell: ({ row }) => (
        <span className="text-[12px] text-[#667085]">{row.original.date}</span>
      ),
    },
    {
      id: "numberOfCVs",
      header: "Number of CVs",
      meta: { className: "flex-1 text-[12px] text-[#667085]" },
      cell: ({ row }) => (
        <span className="text-[12px] text-[#667085]">{row.original.numberOfCVs}</span>
      ),
    },
    {
      id: "status",
      header: "Status",
      meta: { className: "flex-1" },
      cell: ({ row }) => (
        <span
          className={`${styles.status} ${
            styles[row.original.status.toLowerCase().replace(" ", "-")]
          }`}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { className: `${styles.menuSec}` },
      cell: ({ row }) => {
        const options: DropdownMenuOption[] = [
          {
            text: row.original.status === 'Drafts' ? 'Continue' : 'Review Talents',
            onClick: () => handleReviewTalents(row.original.id, row.original.status, row.original.cvAnalysisId),
            key: "review",
          },
        ];
        return (
          <span onClick={(e) => e.stopPropagation()}>
            <DropdownMenu options={options} ellipsisClassName={styles.ellipsis} />
          </span>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={tableBodyItems}
      pagination={pagination}
      emptyState={emptyState}
      rowBaseClassName={(_, index) =>
        `text-[12px] border-t border-[#EAECF0] ${
          index % 2 === 0 ? "bg-[#F9FAFB]" : "bg-white"
        }`
      }
      cellBaseClassName="px-6 py-4"
      headerCellClassName="px-6 py-3 text-left"
      headerClassName="bg-white text-[#1D2939] font-medium text-[14px]"
    />
  );
};

export { CVAnalysisTable };
